// src/services/agreementsAPI.ts — click-to-accept Terms, Order Forms, invoice pay links, billing standing.
import type { PaymentPlan, ScheduledPayment } from '../utils/paymentSchedule';
import http from './http/index';

export type LegalDocSlug = 'terms' | 'dpa' | 'user-terms';

export interface PublicLegalDocument {
    docType: 'tos' | 'dpa' | 'user_terms';
    version: string;
    effectiveDate: string;
    title: string;
    bodyMarkdown: string;
    contentHash: string;
    changeSummary?: string;
    permanentUrl: string;
}

export interface LegalDocumentVersion {
    version: string;
    title: string;
    effectiveDate: string;
    effectiveUntil: string | null;
    contentHash: string;
    changeSummary?: string;
    permanentUrl: string;
}

/** How a new account is set up (Contract Details on account creation). */
export type EntitySetupMethod = 'docusign' | 'order_form';

export interface EntitySetupOptions {
    orderForm: { available: boolean; reason: string | null };
    paperThresholdUsd: number;
}

export interface PostalAddress {
    addressLine1?: string;
    addressLine2?: string;
    cityTown?: string;
    stateProvinceRegion?: string;
    postalCode?: string;
    country?: string;
}

export interface OrderFormCommercials {
    serviceTier: string;
    headcountBand: string;
    deploymentType: 'pilot' | 'full';
    serviceStartDate: string | null;
    initialTermMonths: number;
    billingFrequency: 'Annual' | 'Monthly';
    annualFee: number;
    onboardingFee: number;
    totalInitial: number;
    renewalFee: number;
    paymentPlan?: PaymentPlan;
    /** The annual fee in payments; onboarding is added to the first. Empty: one annual payment. */
    paymentSchedule?: ScheduledPayment[];
}

export interface OrderFormLegalDoc {
    docType: string;
    version: string;
    title: string;
    effectiveDate: string;
    bodyMarkdown: string;
    contentHash: string;
    permanentUrl: string;
}

export interface PublicOrderForm {
    orderFormNumber: string;
    issuedAt: string;
    status: 'sent' | 'accepted' | 'void' | 'expired';
    commercials: OrderFormCommercials;
    tos: OrderFormLegalDoc | null;
    dpa: OrderFormLegalDoc | null;
    prefill: {
        companyName: string;
        principalAddress: PostalAddress;
        billingAddress: PostalAddress;
        billingContactName: string;
        billingContactEmail: string;
        acceptorName: string;
        acceptorEmail: string;
    };
    accepted: { acceptedAt: string; acceptorName: string; legalName: string; payUrl: string | null } | null;
}

export interface AcceptOrderFormInput {
    legalName: string;
    entityType: string;
    stateOfFormation: string;
    principalAddress: PostalAddress;
    billingAddress?: PostalAddress | null;
    billingContactName: string;
    billingContactEmail: string;
    noticeEmail: string;
    acceptorName: string;
    acceptorTitle: string;
    acceptorEmail: string;
    agreed: boolean;
    acceptanceText: string;
    clientTimeZone?: string;
}

export interface AcceptOrderFormResult {
    acceptanceId: string;
    payUrl: string | null;
    alreadyAccepted: boolean;
    /** What was actually emailed on this acceptance (absent on a repeat submit). */
    emails?: { confirmation: boolean; signInLink: boolean; signInLinkToAcceptor: boolean };
}

/** The success message, built from what was actually sent. */
export function acceptedMessage(result: AcceptOrderFormResult): string {
    if (result.alreadyAccepted || !result.emails) return 'This Order Form was already accepted. A confirmation was emailed at the time.';
    const { confirmation, signInLink, signInLinkToAcceptor } = result.emails;
    const first = confirmation
        ? signInLink && signInLinkToAcceptor
            ? "We've emailed you a confirmation and your sign-in link."
            : "We've emailed you a confirmation."
        : "We couldn't email your confirmation just now; your Aptive Index advisor will follow up.";
    const second = signInLink
        ? signInLinkToAcceptor && confirmation
            ? ''
            : signInLinkToAcceptor
              ? ' Your sign-in link was emailed to you.'
              : ' The sign-in link went to your account administrator.'
        : ' Your sign-in link will follow from your Aptive Index advisor.';
    return `Accepted. ${first}${second}`;
}

export interface PublicInvoiceView {
    invoiceNumber: string;
    companyName: string;
    invoiceDate: string;
    dueDate: string | null;
    lines: Array<{ description: string; amount: number }>;
    total: number;
    amountPaid: number;
    amountDue: number;
    status: string;
    paidAt: string | null;
    paymentProcessingAt: string | null;
    lastPaymentError: string | null;
    isPastDue: boolean;
    remittance: {
        bankName?: string;
        accountName?: string;
        routingNumber?: string;
        accountNumber?: string;
        note?: string;
    } | null;
}

export interface UpcomingTerms {
    version: string;
    effectiveDate: string;
    changeSummary: string | null;
    url: string;
}

export interface AgreementStatus {
    mode: 'click' | 'paper' | 'legacy' | null;
    status: 'pending_acceptance' | 'accepted' | null;
    pendingAcceptance: boolean;
    suspended?: boolean;
    /** QA sandbox account: the gate also applies during "log in as", and a SANDBOX stripe shows. */
    isSandbox?: boolean;
    /** Org admins only: a newer Terms version is in force and hasn't been accepted yet. */
    reaccept?: { required: boolean; version?: string; legalName?: string; dpaVersion?: string | null; url?: string };
    /** Org admins only: a published Terms version inside its notice period. */
    upcomingTerms?: UpcomingTerms | null;
    /** The user's other active orgs, sent only while this one is held or paused. */
    otherOrgs?: Array<{ entityId: string; name: string }>;
}

export interface AccountAgreementSummary {
    accountName: string;
    agreement: {
        mode: 'click' | 'paper' | 'legacy';
        status: 'pending_acceptance' | 'accepted' | null;
        legalName: string | null;
        acceptedAt: string | null;
        acceptedBy: { name: string; title: string } | null;
        method: 'checkbox_and_button' | 'paper_signature' | null;
        tosVersion: string | null;
        tosUrl: string | null;
        dpaUrl: string | null;
        hasOrderFormPdf: boolean;
    };
    currentTerms: { version: string; url: string } | null;
    upcomingTerms: UpcomingTerms | null;
    noticeEmail: string | null;
    billingContact: { name: string | null; email: string | null };
    renewal: {
        termEnd: string | null;
        renewalFee: number;
        nonRenewalDeadline: string | null;
        canElectNonRenewal: boolean;
        nonRenewalElected: boolean;
        nonRenewalAt: string | null;
    };
    invoices: Array<{
        invoiceId: string;
        invoiceNumber: string;
        invoiceDate: string;
        dueDate: string | null;
        total: number;
        amountPaid: number;
        status: string;
        payUrl: string;
    }>;
}

export interface OpenInvoiceSummary {
    invoiceId: string;
    invoiceNumber: string;
    amountDue: number;
    dueDate: string | null;
    status: string;
    daysPastDue: number;
    /** Relative SPA path, e.g. `/pay/<token>`. */
    payUrl: string;
}

export interface BillingStanding {
    isOrgAdmin: boolean;
    suspended: boolean;
    suspendedReason?: string | null;
    pastDue: boolean;
    showBanner: boolean;
    openInvoices: OpenInvoiceSummary[];
}

export interface AdminInvoiceSummary {
    invoiceId: string;
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string | null;
    total: number;
    amountPaid: number;
    status: string;
    paymentMethodType: string | null;
    daysPastDue: number;
    /** Absolute URL. */
    payUrl: string;
}

export interface AdminOrderForm {
    _id: string;
    orderFormNumber: string;
    status: 'sent' | 'viewed' | 'accepted' | 'void';
    commercials: OrderFormCommercials;
    sentTo: { name?: string; email: string };
    sentAt: string | null;
    viewedAt: string | null;
    acceptedAt: string | null;
    voidedAt: string | null;
    voidReason: string | null;
    tokenExpiresAt: string;
    invoiceId: string | null;
    /** Why the invoice wasn't created after acceptance, if it wasn't. */
    invoiceError?: string | null;
    payUrl: string | null;
    createdAt: string;
}

export interface AdminAcceptance {
    _id: string;
    orderFormNumber: string | null;
    acceptor: { name: string; title: string; email: string };
    client: { legalName: string; noticeEmail: string; billingContactEmail: string };
    acceptedAt: string;
    ipAddress?: string;
    tos: { version: string; contentHash: string };
    dpa?: { version: string; contentHash: string } | null;
    method: 'checkbox_and_button' | 'paper_signature';
    signedDocumentUrl?: string | null;
}

export interface AdminNotice {
    _id: string;
    kind: string;
    channel: string;
    to: string[];
    subject?: string;
    sentAt: string;
    error?: string | null;
    stepKey?: string | null;
}

export interface AgreementOverview {
    agreement: {
        mode?: 'click' | 'paper' | 'legacy' | null;
        status?: 'pending_acceptance' | 'accepted' | null;
        acceptedAt?: string | null;
        legalName?: string | null;
        noticeEmail?: string | null;
        welcomeEmailSentAt?: string | null;
    } | null;
    suggestedMode: 'click' | 'paper';
    isSandbox?: boolean;
    orderForms: AdminOrderForm[];
    acceptances: AdminAcceptance[];
    notices: AdminNotice[];
    noticeEmailHistory: Array<{ from: string | null; to: string; changedAt: string }>;
    billing?: {
        billingStanding?: { suspensionEligible?: boolean; suspensionEligibleAt?: string | null } | null;
        suspension?: {
            active?: boolean;
            reason?: string | null;
            suspendedAt?: string | null;
            liftOnPayment?: boolean;
        } | null;
        suspensionHistory?: Array<Record<string, unknown>>;
        invoices?: AdminInvoiceSummary[];
    };
}

export type PaperAcceptanceInput = Omit<AcceptOrderFormInput, 'agreed' | 'acceptanceText' | 'clientTimeZone'> & {
    signedAt: string;
    signedDocumentUrl: string;
};

/** Pulls `{ error }` out of an API error for inline display. */
export function apiErrorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
    const data = (err as { response?: { data?: unknown } })?.response?.data;
    if (data && typeof data === 'object' && typeof (data as { error?: unknown }).error === 'string') {
        return (data as { error: string }).error;
    }
    if (typeof data === 'string' && data) return data;
    return fallback;
}

export function apiErrorStatus(err: unknown): number | undefined {
    return (err as { response?: { status?: number } })?.response?.status;
}

function enc(value: string): string {
    return encodeURIComponent(value);
}

const agreementsAPI = {
    // Public
    getLegalDocument: (slug: string) => http.get<PublicLegalDocument>(`/api/legal/${enc(slug)}`).then((r) => r.data),
    getLegalDocumentVersion: (slug: string, version: string) =>
        http.get<PublicLegalDocument>(`/api/legal/${enc(slug)}/v/${enc(version)}`).then((r) => r.data),
    listLegalDocumentVersions: (slug: string) =>
        http.get<{ docType: string; versions: LegalDocumentVersion[] }>(`/api/legal/${enc(slug)}/versions`).then((r) => r.data),
    getOrderForm: (token: string) => http.get<PublicOrderForm>(`/api/order-forms/${enc(token)}`).then((r) => r.data),
    acceptOrderForm: (token: string, input: AcceptOrderFormInput) =>
        http.post<AcceptOrderFormResult>(`/api/order-forms/${enc(token)}/accept`, input).then((r) => r.data),
    getPayInvoice: (token: string) => http.get<PublicInvoiceView>(`/api/pay/${enc(token)}`).then((r) => r.data),
    startCheckout: (token: string) => http.post<{ url: string }>(`/api/pay/${enc(token)}/checkout`, {}).then((r) => r.data),

    // Signed-in portal users
    getAgreementStatus: () => http.get<AgreementStatus>('/admin/agreements/status').then((r) => r.data),
    getBillingStanding: () => http.get<BillingStanding>('/admin/billing/standing').then((r) => r.data),
    getSetupOptions: () => http.get<EntitySetupOptions>('/admin/agreements/setup-options').then((r) => r.data),

    // Organization Administrators (Settings → Agreement & Billing)
    getMyAgreement: () => http.get<AccountAgreementSummary>('/admin/agreements/mine').then((r) => r.data),
    downloadMyOrderFormPdf: () =>
        http.get<Blob>('/admin/agreements/mine/order-form.pdf', { responseType: 'blob' }).then((r) => r.data),
    updateNoticeEmail: (email: string) =>
        http.put<{ noticeEmail: string }>('/admin/agreements/mine/notice-email', { email }).then((r) => r.data),
    electNonRenewal: () =>
        http.post<{ termEnd: string; alreadyElected: boolean }>('/admin/agreements/mine/non-renewal', { confirm: true }).then((r) => r.data),
    reacceptTerms: (body: { acceptorTitle: string; agreed: boolean; acceptanceText: string; clientTimeZone?: string }) =>
        http.post<{ acceptanceId: string; alreadyCurrent: boolean }>('/admin/agreements/mine/reaccept', body).then((r) => r.data),

    // God Mode
    getOverview: (entityId: string) =>
        http.get<AgreementOverview>(`/admin/agreements/entity/${enc(entityId)}`).then((r) => r.data),
    issueOrderForm: (
        entityId: string,
        body: { overrides?: Partial<OrderFormCommercials>; sendTo?: { name?: string; email: string } } = {}
    ) => http.post<AdminOrderForm>(`/admin/agreements/entity/${enc(entityId)}/order-forms`, body).then((r) => r.data),
    voidOrderForm: (orderFormId: string, reason: string) =>
        http.post<AdminOrderForm>(`/admin/agreements/order-forms/${enc(orderFormId)}/void`, { reason }).then((r) => r.data),
    createMissingInvoice: (orderFormId: string) =>
        http.post<{ invoiceNumber: string }>(`/admin/agreements/order-forms/${enc(orderFormId)}/create-invoice`, {}).then((r) => r.data),
    recordPaperAcceptance: (entityId: string, input: PaperAcceptanceInput) =>
        http.post<AdminAcceptance>(`/admin/agreements/entity/${enc(entityId)}/paper-acceptance`, input).then((r) => r.data),
    resendWelcome: (entityId: string) =>
        http.post<{ sent: boolean }>(`/admin/agreements/entity/${enc(entityId)}/resend-welcome`, {}).then((r) => r.data),
    exportAcceptance: (acceptanceId: string, format: 'pdf' | 'json') =>
        http
            .get<Blob>(`/admin/agreements/acceptances/${enc(acceptanceId)}/export`, {
                params: { format },
                responseType: 'blob',
            })
            .then((r) => r.data),
    suspendEntity: (entityId: string, reason: string, liftOnPayment = true) =>
        http.post(`/admin/agreements/entity/${enc(entityId)}/suspend`, { reason, liftOnPayment }).then((r) => r.data),
    restoreEntity: (entityId: string) =>
        http.post(`/admin/agreements/entity/${enc(entityId)}/restore`, {}).then((r) => r.data),
};

export default agreementsAPI;
